import React, { useState, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useFamilyFeatures } from '@/hooks/useFamilyFeatures'
import toast from 'react-hot-toast'
import { Gift, DollarSign, ShoppingCart, Trophy, Zap, Star, TrendingUp, CreditCard, Settings } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { isSharedSessionActive } from '@/utils/sharedDevice'
import {
  useRewards,
  useRedemptions,
  useGreenlightTransfers,
  useRedeemReward,
  Reward,
  RewardRedemption,
  GreenlightTransfer,
} from '@/hooks/useRewards'

export default function RewardsStore() {
  const [activeTab, setActiveTab] = useState<'store' | 'history'>('store')
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'money' | 'privilege' | 'item' | 'experience'>('all')
  const { user } = useAuth()
  const { isFeatureEnabled } = useFamilyFeatures()

  const greenlightEnabled = isFeatureEnabled('greenlight')

  // React Query hooks for data fetching
  const { data: allRewards = [], isLoading: rewardsLoading } = useRewards()
  const { data: redemptions = [], isLoading: redemptionsLoading } = useRedemptions()
  const { data: transfers = [] } = useGreenlightTransfers(user?.id, greenlightEnabled)
  const redeemMutation = useRedeemReward()

  // Filter rewards based on greenlight status
  const rewards = useMemo(() => {
    return allRewards.filter((r: Reward) => {
      if (!r.active) return false
      if (!greenlightEnabled && r.category === 'money') return false
      return true
    })
  }, [allRewards, greenlightEnabled])

  const isLoading = rewardsLoading || redemptionsLoading

  const handleRedeemReward = async (rewardId: string) => {
    const reward = rewards.find(r => r.id === rewardId)
    if (!reward) return

    const availablePoints = user?.available_points ?? 0
    if (!user || availablePoints < reward.cost_points) {
      toast.error('Not enough points!')
      return
    }

    // Confirm redemption
    const confirmed = window.confirm(
      `Redeem "${reward.name}" for ${reward.cost_points} points?\n\n` +
      `You have ${availablePoints} available points.\n` +
      `After redemption: ${availablePoints - reward.cost_points} points`
    )

    if (!confirmed) return

    try {
      const result = await redeemMutation.mutateAsync({
        reward_id: rewardId,
        notes: ''
      })

      // Redemptions from a shared family device always wait for a parent.
      if (isSharedSessionActive() && (result?.status === 'pending' || result?.requires_parent_approval)) {
        toast.success('Sent to a parent for approval')
        return
      }

      toast.success(
        reward.requires_parent_approval || result?.status === 'pending'
          ? 'Redemption submitted for parent approval!'
          : 'Reward redeemed successfully!'
      )
      // No need to manually reload - React Query handles cache invalidation
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to redeem reward')
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'money': return <DollarSign className="w-5 h-5" />
      case 'privilege': return <Star className="w-5 h-5" />
      case 'item': return <Gift className="w-5 h-5" />
      case 'experience': return <Trophy className="w-5 h-5" />
      default: return <ShoppingCart className="w-5 h-5" />
    }
  }

  const getRewardIcon = (iconString: string): string => {
    // Map Material Design Icons (mdi:) strings to emoji characters
    const iconMap: Record<string, string> = {
      'mdi:gift': '🎁',
      'mdi:shopping_cart': '🛒',
      'mdi:cart': '🛒',
      'mdi:trophy': '🏆',
      'mdi:star': '⭐',
      'mdi:dollar': '💵',
      'mdi:cash': '💵',
      'mdi:money': '💵',
      'mdi:gamepad': '🎮',
      'mdi:controller': '🎮',
      'mdi:pizza': '🍕',
      'mdi:movie': '🎬',
      'mdi:ice_cream': '🍦',
      'mdi:icecream': '🍦',
      'mdi:candy': '🍬',
      'mdi:book': '📚',
      'mdi:music': '🎵',
      'mdi:headphones': '🎧',
      'mdi:bike': '🚲',
      'mdi:rocket': '🚀',
      'mdi:heart': '❤️',
      'mdi:fire': '🔥',
      'mdi:lightning': '⚡',
      'mdi:crown': '👑',
      'mdi:gem': '💎',
      'mdi:palette': '🎨',
      'mdi:camera': '📷',
      'mdi:phone': '📱',
      'mdi:laptop': '💻',
      'mdi:watch': '⌚',
      'mdi:ticket': '🎫',
      'mdi:popcorn': '🍿',
      'mdi:hamburger': '🍔',
      'mdi:cookie': '🍪',
      'mdi:cake': '🎂',
      'mdi:cupcake': '🧁',
      'mdi:soccer': '⚽',
      'mdi:basketball': '🏀',
      'mdi:football': '🏈',
      'mdi:baseball': '⚾',
      'mdi:tennis': '🎾',
      'mdi:swimming': '🏊',
      'mdi:bicycle': '🚴',
      'mdi:run': '🏃',
      'mdi:beach': '🏖️',
      'mdi:sun': '☀️',
      'mdi:umbrella': '☂️',
      'mdi:snowflake': '❄️',
      'mdi:tree': '🌲',
      'mdi:flower': '🌸',
      'mdi:car': '🚗',
      'mdi:airplane': '✈️',
      'mdi:train': '🚂',
      'mdi:boat': '⛵'
    }

    // Return mapped emoji or fallback to original string or default gift emoji
    return iconMap[iconString.toLowerCase()] || iconString || '🎁'
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100'
      case 'approved': return 'text-blue-600 bg-blue-100'
      case 'delivered': return 'text-green-600 bg-green-100'
      case 'rejected': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const filteredRewards = selectedCategory === 'all' 
    ? rewards 
    : rewards.filter(r => r.category === selectedCategory)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading rewards...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Points Balance Banner */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg p-4">
        <div className="flex items-center justify-between">
          <p className="text-blue-100">Spend your hard-earned points!</p>
          <div className="flex items-center gap-3">
            <div className="text-sm text-blue-100">Available:</div>
            <div className="text-2xl font-bold">{user?.available_points?.toLocaleString() || 0}</div>
            <div className="text-sm text-blue-100">points</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('store')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'store'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ShoppingCart className="w-4 h-4 inline mr-2" />
          Store
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'history'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <TrendingUp className="w-4 h-4 inline mr-2" />
          History
        </button>
      </div>

      {/* Store Tab */}
      {activeTab === 'store' && (
        <div>
          {/* Category Filter */}
          <div className="flex space-x-2 mb-6 overflow-x-auto">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                selectedCategory === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All Rewards
            </button>
            <button
              onClick={() => setSelectedCategory('money')}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                selectedCategory === 'money'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              💵 Money
            </button>
            <button
              onClick={() => setSelectedCategory('privilege')}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                selectedCategory === 'privilege'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ⭐ Privileges
            </button>
            <button
              onClick={() => setSelectedCategory('item')}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                selectedCategory === 'item'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🎁 Items
            </button>
            <button
              onClick={() => setSelectedCategory('experience')}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                selectedCategory === 'experience'
                  ? 'bg-pink-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🏆 Experiences
            </button>
          </div>

          {/* Money Exchange Rate Notice */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <DollarSign className="w-6 h-6 text-green-600 mr-3" />
              <div>
                <h3 className="font-semibold text-green-900">Points to Money Exchange</h3>
                <p className="text-green-800 text-sm">10 points = $1.00 (transferred to your Greenlight card)</p>
              </div>
            </div>
          </div>

          {/* Rewards Grid */}
          {filteredRewards.length === 0 ? (
            allRewards.length === 0 ? (
              user?.is_parent ? (
                <EmptyState
                  icon={Gift}
                  title="No rewards created yet"
                  description="Create rewards for your kids to redeem with their earned points. Set point costs and approval requirements for each reward."
                  actionLabel="Manage Rewards"
                  actionHref="/dashboard/rewards?tab=manage"
                />
              ) : (
                <EmptyState
                  icon={Gift}
                  title="No rewards available yet"
                  description="Your parents haven't set up rewards yet. Keep completing chores to earn points - rewards are coming soon!"
                />
              )
            ) : (
              <div className="card text-center py-12">
                <div className="text-4xl mb-2">🏪</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No rewards in this category
                </h3>
                <p className="text-gray-600">
                  Try selecting a different category above.
                </p>
              </div>
            )
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredRewards.map((reward) => (
                <div key={reward.id} className="card hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{getRewardIcon(reward.icon)}</span>
                      {getCategoryIcon(reward.category)}
                    </div>
                    {reward.availability === 'limited' && reward.stock_remaining && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">
                        Only {reward.stock_remaining} left!
                      </span>
                    )}
                  </div>

                  <h3 className="font-semibold text-gray-900 mb-1">{reward.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{reward.description}</p>

                  {reward.category === 'money' && reward.value_in_cents && (
                    <div className="text-sm text-green-600 mb-3">
                      Value: ${(reward.value_in_cents / 100).toFixed(2)}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex items-center space-x-1">
                      <Zap className="w-4 h-4 text-yellow-500" />
                      <span className="font-bold text-lg">{reward.cost_points}</span>
                      <span className="text-sm text-gray-600">points</span>
                    </div>

                    <button
                      onClick={() => handleRedeemReward(reward.id)}
                      disabled={!user || (user.available_points ?? 0) < reward.cost_points}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        user && (user.available_points ?? 0) >= reward.cost_points
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Redeem
                    </button>
                  </div>

                  {reward.requires_parent_approval && (
                    <div className="text-xs text-yellow-600 mt-2">
                      ⚠️ Requires parent approval
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          {/* Greenlight Transfers Section */}
          {greenlightEnabled && transfers.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                Greenlight Transfers
              </h2>
              <div className="space-y-3">
                {transfers.map((transfer) => (
                  <div key={transfer.id} className="card border-l-4 border-green-500">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xl">💳</span>
                          <h3 className="font-semibold text-gray-900">
                            Greenlight Transfer
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          Points converted to ${transfer.dollar_amount.toFixed(2)} on your card
                        </p>
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-gray-500">
                            {new Date(transfer.created_at).toLocaleDateString()}
                          </span>
                          <span className="px-2 py-1 rounded-full text-xs font-medium text-green-600 bg-green-100">
                            Completed
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center space-x-1 text-green-600">
                          <DollarSign className="w-4 h-4" />
                          <span className="font-bold text-lg">{transfer.dollar_amount.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-gray-500 text-sm">
                          <Zap className="w-3 h-3 text-yellow-500" />
                          <span>{transfer.points_converted} pts</span>
                        </div>
                      </div>
                    </div>
                    {transfer.notes && (
                      <div className="mt-2 text-sm text-gray-600 italic">
                        Note: {transfer.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Redemptions Section */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-600" />
              Reward Redemptions
            </h2>

            {redemptions.length === 0 ? (
              <EmptyState
                icon={ShoppingCart}
                title="No redemptions yet"
                description={user?.is_parent
                  ? "When your kids redeem rewards, their history will appear here."
                  : "Once you redeem rewards from the store, you'll see your history here. Earn points by completing chores!"}
                variant="compact"
              />
            ) : (
              <div className="space-y-3">
                {redemptions.map((redemption) => (
                  <div key={redemption.id} className="card">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xl">{getRewardIcon(redemption.reward.icon)}</span>
                          <h3 className="font-semibold text-gray-900">
                            {redemption.reward.name}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {redemption.reward.description}
                        </p>
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-gray-500">
                            {new Date(redemption.created_at).toLocaleDateString()}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(redemption.status)}`}>
                            {redemption.status.charAt(0).toUpperCase() + redemption.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center space-x-1">
                          <Zap className="w-4 h-4 text-yellow-500" />
                          <span className="font-bold">{redemption.points_spent}</span>
                        </div>
                      </div>
                    </div>
                    {redemption.notes && (
                      <div className="mt-2 text-sm text-gray-600 italic">
                        Note: {redemption.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  )
}